import { ParsedUrlQuery } from 'querystring';

import { KeyboardEvent, useMemo, useState } from 'react';

import dynamic from 'next/dynamic';
import { debounce } from 'lodash-es';
import { useSearchParams } from 'next/navigation';

import {
  SearchViewModel,
  MetaViewModel,
  HotelListFiltersViewModel,
  PackageListHotelViewModel,
} from '@api/v3';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import { SearchIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { DateType, formatMomentDate } from '@utils/dateUtil';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import StickyElement from '@components/utils/StickyElement';
import { useDevice } from '@contexts/device';
import { getLoyaltyTypeFromUtmSource } from '@components/ui/loyalty-verifications/helpers';
import SearchReservedId from '@components/ui/SearchReservedId';
import { useKocAliemContext } from '@contexts/loyalty/KocAilem';
import Typography from '@components/Typography';
import ResponsiveListingFilter from '@components/views/hotel/listing/components/ResponsiveFilterAndSort/ResponsiveListingFilterAndSort';
import {
  ResponsiveBox,
  ResponsiveSearchBoxEdit,
  ResponsiveSearchBoxEditLabel,
  ResponsiveSearchBoxWrapper,
  ResponsiveBoxAffix,
  ResponsiveBoxContainer,
  SearchIconWrapper,
  ButtonsWrapper,
} from '@components/views/hotel/listing/HotelListingStyle';
import ResponsiveSearch from '@components/views/hotel/listing/components/ResponsiveSearch/ResponsiveSearch';
import CyprusTab from '@components/views/package/detail/components/package-hotel-selection-tab';
import { paramsToObject } from '@components/views/utils';
import { useCalendarHoliday } from '@components/DatePicker/DateRangePicker/getCalendarHolidays';

const KocAilemBanner = dynamic(
  () =>
    import('@components/views/hotel/listing/components/KocAilem/KocAilemBanner')
);

interface ResponsiveListingBoxProps {
  suggestionLocation?: string;
  location?: SearchViewModel;
  hotelRoomsList?: Array<ListingSearchRoomInterface>;
  meta?: MetaViewModel;
  isModalVisible: boolean;
  toggleIsModalVisible: () => void;
  startDate?: DateType | null;
  endDate?: DateType | null;
  sticky?: boolean;
  onResearchClick?: () => void;
  hideModal?: () => void;
  shouldShallowPush?: boolean;
  detail?: boolean;
  hotelFilters?: HotelListFiltersViewModel[];
  isFetching?: boolean;
  isExpediaHotels?: boolean;
  hotels?: (PackageListHotelViewModel[] | undefined)[];
  currentPage?: number;
  selectedTab?: string;
  setSelectedTab?: React.Dispatch<React.SetStateAction<string>>;
  setIsRedirecting?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ListingMetaInterface {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  title?: string;
  description?: string;
}

const ResponsiveListingBox = ({
  hotelRoomsList,
  meta,
  isModalVisible,
  toggleIsModalVisible,
  startDate,
  endDate,
  sticky = true,
  onResearchClick,
  hideModal,
  shouldShallowPush,
  location,
  detail,
  hotelFilters,
  isFetching,
  isExpediaHotels,
  hotels,
  currentPage,
  selectedTab,
  setSelectedTab,
  setIsRedirecting,
}: ResponsiveListingBoxProps) => {
  const { isDesktop } = useDevice();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const { isStickySearch, setIsStickySearch } = useSearchBarContext();
  const [affixedListingBox, setIsAffixedListingBox] = useState(false);

  const roomsCount = hotelRoomsList?.length;
  const adultNumber = hotelRoomsList?.reduce((t, a) => t + a.adultCount, 0);
  const childrenCount = hotelRoomsList?.reduce(
    (t, a) => t + a.childrenList.length,
    0
  );
  const loyaltyType = getLoyaltyTypeFromUtmSource(query?.utm_source as string);

  const roomSelectBoxLabel = ` ${roomsCount} Oda, ${adultNumber} Yetişkin ${
    childrenCount && childrenCount > 0 ? ', ' + childrenCount + ' Çocuk' : ''
  }`;
  const { isValidated: isKocAilemValidate } = useKocAliemContext();

  const onKeyPress = debounce((e: KeyboardEvent<HTMLDivElement>): void => {
    const enterOrSpace =
      e.key === 'Enter' ||
      e.key === ' ' ||
      e.key === 'Spacebar' ||
      e.which === 13 ||
      e.which === 32;
    if (enterOrSpace) {
      e.preventDefault();
      toggleIsModalVisible();
    }
  }, 200);
  const { data: initialData } = useCalendarHoliday();
  return (
    <ResponsiveBoxAffix
      sticky={sticky}
      onChange={(affixed) => setIsAffixedListingBox(affixed)}
    >
      <SearchReservedId show={sticky && affixedListingBox} />
      {isModalVisible ? (
        <ResponsiveSearch
          initialData={initialData}
          setIsRedirecting={setIsRedirecting}
          detail={detail}
          isShowModal={isModalVisible}
          hideModal={hideModal || toggleIsModalVisible}
          onResearchClick={onResearchClick}
          shouldShallowPush={shouldShallowPush}
        />
      ) : null}
      <ResponsiveBox>
        <ResponsiveBoxContainer>
          <CyprusTab
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
          <StickyElement onStickyStateChange={setIsStickySearch} max={300} />
          <div>
            {isDesktop ? (
              <Typography variant='Title5' color='BR300' weight='SemiBold'>
                {location?.text || ''}
              </Typography>
            ) : (
              <Typography variant='caption3' color='NL800' weight='SemiBold'>
                {location?.text || ''}
              </Typography>
            )}
            {meta && meta.totalCount > 0 && location?.text ? (
              <Typography variant='caption3' color='NL800' weight='SemiBold'>
                ({meta.totalCount})
              </Typography>
            ) : null}
          </div>
          <div>
            <Typography
              variant='caption3'
              weight='Regular'
              color='NL500'
              as='div'
            >
              {startDate
                ? `${formatMomentDate(startDate)} ${
                    endDate ? `- ${formatMomentDate(endDate)}` : ''
                  }`
                : `Giriş - Çıkış Tarihleri`}
            </Typography>

            <Typography variant='caption3' weight='Regular' color='NL500'>
              {roomSelectBoxLabel}
            </Typography>
          </div>
          <ResponsiveSearchBoxWrapper>
            <ButtonsWrapper>
              <ResponsiveListingFilter
                select='hotel'
                hotelFilters={hotelFilters}
                filters={hotelFilters}
                isLoading={isFetching}
                hotels={hotels?.[currentPage || 0]}
                isExpedia={isExpediaHotels}
              />
              <ResponsiveSearchBoxEdit
                role='button'
                tabIndex={0}
                onKeyPress={onKeyPress}
                onClick={toggleIsModalVisible}
              >
                <SearchIconWrapper>
                  <CustomIcon
                    icon={SearchIcon}
                    fontSize={16}
                    paletteColor='B300'
                  />
                </SearchIconWrapper>
                <ResponsiveSearchBoxEditLabel>
                  <Typography variant='caption3' weight='Regular' color='B300'>
                    Yeniden Ara
                  </Typography>
                </ResponsiveSearchBoxEditLabel>
              </ResponsiveSearchBoxEdit>
            </ButtonsWrapper>
          </ResponsiveSearchBoxWrapper>
          {loyaltyType && isStickySearch && !isKocAilemValidate ? (
            <KocAilemBanner showInfoText={false} />
          ) : null}
        </ResponsiveBoxContainer>
      </ResponsiveBox>
    </ResponsiveBoxAffix>
  );
};

export default ResponsiveListingBox;
